<template>
  <div>
    <!-- Reflow Dependencies - Force this component's update hook to fire when these values change -->
    <!-- This is needed for smooth reflow. Otherwise, only BaseModal's update() hook will fire -->
    <span class="hidden reflow-depencencies">
      {{ stepIndex }}
    </span>
    <BaseModal @close="$emit('close')" cardWidth="600px" noPadding noToolbar noExit>
      <template #default>
        <!-- HEADER -->
        <div class="relative w-full flex gap-2 p-5 border-b border-border-normal">
          <PlusSquareIcon class="text-icon-muted flex-shrink-0" />
          <div class="flex-grow flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Create {{ stepIndex > 0 && reportForm.type?.name || 'New' }} Report
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Select the report type, ad accounts, and enter a name and description.
            </BaseText>
          </div>
          <button class="absolute top-3 right-3 w-6 h-6 flex items-center justify-center"
          @click="$emit('close')">
            <TagRemoveIcon stroke="#5A667C" :width="16" :height="16" />
          </button>
        </div>
        <!-- CONTENT -->
        <div ref="modalContent" class="w-full p-5 border-b border-border-normal">
          <!-- REPORT TYPE SELECTION -->
          <div v-if="currentStep === 'REPORT_TYPE_SELECTION'" class="w-full flex flex-col gap-2 fade-in" key="report-type-selection">
            <BaseText type="label" size="sm" class="text-text-muted">
              Report Type
            </BaseText>
            <div class="w-full grid grid-cols-2 gap-3">
              <button v-for="reportType of reportTypes" :key="reportType.key" 
              class="report-type-btn w-full flex flex-col items-center p-4 text-center" 
              :class="{'active': reportType.key === reportForm.type.key}"
              @click="reportForm.type = reportType">
                <component :is="reportType.icon" class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted mt-3">
                  {{ reportType.name }}
                </BaseText>
                <BaseText type="body" size="sm" class="text-text-normal mt-1">
                  {{ reportType.desc }}
                </BaseText>
              </button>
            </div>
          </div>
          <!-- AD ACCOUNT SELECTION -->
          <div v-if="currentStep === 'AD_ACCOUNT_SELECTION'" class="w-full h-full flex flex-col fade-in" key="ad-account-selection">
            <div class="w-full flex items-center gap-1.5 flex-shrink-0">
              <BaseText type="label" size="sm" class="text-text-muted">
                Select Ad Accounts
              </BaseText>
              <!-- info tooltip -->
              <div class="relative">
                <ShowMoreInfoIcon class="text-text-disabled transition-colors hover:text-text-subdued"
                @mouseenter.native="showInfoTooltip = true" @mouseleave.native="showInfoTooltip = false" />
                <transition>
                  <div v-if="showInfoTooltip" class="info-tooltip flex flex-col gap-1.5 bg-neutral-700 rounded-md p-2.5 shadow-md z-50">
                    <BaseText type="body" size="xs" class="text-white">
                      Choose which ad accounts connected to this Lens brand you want to display data for.
                    </BaseText>
                    <BaseText type="body" size="xs" class="text-neutral-alpha-650">
                      This can be changed at any time after the report is created.
                    </BaseText>
                    <BaseText type="body" size="xs" class="text-neutral-alpha-650">
                      You may only view data from one platform at a time.
                    </BaseText>
                  </div>
                </transition>
              </div>
              <transition>
                <BaseText v-if="numSelectedAdAccounts > 0" type="body" size="xs" class="ml-auto text-text-subdued">
                  ({{ numSelectedAdAccounts }} Selected)
                </BaseText>
              </transition>
            </div>
            <!-- Ad Account Selector -->
            <div class="w-full flex-grow rounded-xl pr-0.5 border border-border-normal mt-2">
              <!-- Accounts List -->
              <AdAccountMultiSelector
                :key="`ad-account-selector-${platformToggle}`"
                class="fade-in"
                :ad-account-options="connectedAdAccounts"
                :selected-account-ids="reportForm.adAccounts"
                :max-list-height="221"
                show-platform-icons
                disable-search
                list-padding-classes="px-1 py-1"
                usePlatformExclusiveSelectUI
                :current-selection-platform="currentSelectionPlatform"
                @select="handleAdAccountSelect"
              />
            </div>
          </div>
          <!-- REPORT DETAILS FORM -->
          <div v-if="currentStep === 'REPORT_DETAILS_FORM'" class="w-full flex flex-col gap-4 fade-in" key="report-details-form">
            <BaseInputV3 
              label="Report Name"
              placeholder="Pick a name for this report"
              :inputValue="reportForm.name"
              @update:inputValue="reportForm.name = $event"
            />
            <BaseInputV3 
              label="Report Description"
              placeholder="Give a description of this report"
              :inputValue="reportForm.desc"
              @update:inputValue="reportForm.desc = $event"
              optional
            />
            <BaseSingleDropdown 
              class="z-50"
              use-parent-width
              show-label-space
              label="Folder"
              enable-search
              search-placeholder="Search Folders"
              :options="lensFolders"
              :selected-obj="selectedFolder"
              @change="updateSelectedFolder"
            />
          </div>
        </div>
        <!-- ACTIONS FOOTER -->
        <div class="relative w-full flex items-center justify-between p-5">
          <!-- Back/Cancel Button -->
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="handleBack">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ backButtonText }}
            </BaseText>
          </button>
          <!-- Next/Submit Button -->
          <button class="submit-btn flex items-center justify-center py-1.5 rounded-md whitespace-nowrap"
          :class="submitEnabled ? 'enabled' : 'disabled cursor-default'" :disabled="!submitEnabled"
          :style="{ width: submitBtnWidth }" @click="handleNext">
            <BaseLoadingSpinnerCircle v-if="loadingSubmit" :duration="1.2" class="text-icon-disabled" />
            <BaseText v-else type="label" size="sm" class="fade-in" :class="{'pl-2': !isLastStep}" :key="submitButtonText">
              {{ submitButtonText }}
            </BaseText>
            <div class="next-chevron" v-if="!isLastStep && !loadingSubmit">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
              class="absolute left-1">
                <path d="M8.33331 13.3334L11.0774 10.5893C11.4028 10.2639 11.4028 9.73626 11.0774 9.41083L8.33331 6.66675" 
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </button>
          <!-- Step Indicator -->
          <div class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <BaseText type="body" size="sm" class="text-text-subdued">
              {{ stepIndex + 1 }}/{{ totalSteps }}
            </BaseText>
          </div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import LensAPI from '@/api/lens'
import smoothReflow from 'vue-smooth-reflow'
import { enrichLensAdAccountsWithProviderData } from '@/utils/lens/dataSourceUtils'

// Components
import AdAccountMultiSelector from './AdAccountMultiSelector.vue'

// Icons
import PlusSquareIcon from '../globals/Icons/PlusSquareIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import CreateComparativeReportIcon from '../globals/Icons/LensCreateComparisonReportIcon.vue'
import CreatePerformanceReportIcon from '../globals/Icons/SidebarIcons/dropdowns/CreatePerformanceReportItemIcon.vue'
import ShowMoreInfoIcon from '../globals/Icons/ShowMoreInfoIcon.vue'

export default {
  name: 'CreateLensReportModal',
  mixins: [smoothReflow],
  components: {
    AdAccountMultiSelector,
    PlusSquareIcon,
    TagRemoveIcon,
    ShowMoreInfoIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    preSelectedReportType: {
      type: String,
      default: null
    },
    initFolderId: {
      type: String
    }
  },
  data () {
    const reportTypes = [
      {
        key: 'top-performing',
        name: 'Top Performing',
        desc: 'Analyze which ads are currently performing their best.',
        icon: CreatePerformanceReportIcon
      },
      {
        key: 'comparative',
        name: 'Comparative',
        desc: 'Create custom ad groups to compare against each other.',
        icon: CreateComparativeReportIcon
      }
    ]
    
    return {
      // Form States
      reportForm: {
        type: this.preSelectedReportType ? reportTypes.find(rt => rt.key === this.preSelectedReportType) : {},
        adAccounts: [],
        name: '',
        desc: ''
      },
      selectedFolder: null,

      // UI States
      reportTypes,
      stepIndex: this.preSelectedReportType ? 1 : 0,
      platformToggle: 'meta',
      showInfoTooltip: false,
      loadingSubmit: false
    }
  },
  created () {
    const initFolder = this.lensFolders.find(folder => folder.id === this.initFolderId)
    if (initFolder) {
      this.selectedFolder = initFolder
    } else if (this.lensFolders.length) {
      // If we're not creating a report within a folder context, set the init folder to the default folder
      this.selectedFolder = this.lensFolders[0]
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.modalContent,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.modalContent })
  },
  watch: {
    connectedAdAccounts: {
      // If we only have one ad account, set the selected form ad account to it
      // This is done automatically because we don't show the account selector in this case
      handler (accounts) {
        if (accounts?.length === 1) {
          this.reportForm.adAccounts = [accounts[0].id]
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses', 'getLensFolders', 'getProviders']),
    // Data Computed
    lens () {
      return this.getSidebarLenses.find(lens => lens.id === this.lensId)
    },
    lensFolders () {
      const folders = [...(this.getLensFolders?.[this.lensId] || [])]
      if (folders.length) folders[0] = { ...folders[0], name: 'Custom Reports' }
      return folders
    },
    connectedAdAccounts () {
      if (!this.getProviders?.length || !this.lens?.accounts?.length) return []
      return enrichLensAdAccountsWithProviderData(this.getProviders, this.lens.accounts)
    },
    modalSteps () {
      return this.connectedAdAccounts.length > 1
        ? ['REPORT_TYPE_SELECTION', 'AD_ACCOUNT_SELECTION', 'REPORT_DETAILS_FORM']
        : ['REPORT_TYPE_SELECTION', 'REPORT_DETAILS_FORM']
    },
    currentStep () {
      return this.modalSteps[this.stepIndex]
    },
    totalSteps () {
      return this.modalSteps.length
    },
    submitEnabled () {
      let stepValid = false
      if (this.currentStep === 'REPORT_TYPE_SELECTION') stepValid = !!this.reportForm.type?.key
      if (this.currentStep === 'AD_ACCOUNT_SELECTION') stepValid = this.reportForm.adAccounts.length > 0
      if (this.currentStep === 'REPORT_DETAILS_FORM') stepValid = !!(this.reportForm.name.length && this.selectedFolder)
      return stepValid && !this.loadingSubmit
    },
    // UI Computed
    submitButtonText () {
      if (this.isLastStep) return 'Create Report'
      return 'Next'
    },
    backButtonText () {
      if (this.isFirstStep) return 'Cancel'
      return 'Back'
    },
    isFirstStep () {
      return this.stepIndex === 0
    },
    isLastStep () {
      return this.stepIndex === this.totalSteps - 1
    },
    numSelectedAdAccounts () {
      return this.reportForm.adAccounts.length
    },
    submitBtnWidth () {
      return this.isLastStep ? '114px' : '75px'
    },
    currentSelectionPlatform () {
      if (!this.reportForm.adAccounts.length) return null
      return this.connectedAdAccounts.find(account => account.id === this.reportForm.adAccounts[0])?.platform
    }
  },
  methods: {
    // ====== Data Methods ======
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async handleSubmit () {
      if (!this.submitEnabled) return
      const { name, desc, type, adAccounts } = this.reportForm
      if (!adAccounts.length) { // Should never happen, but just in case
        this.$showAlert({ 
          type: 'error', 
          message: 'Error preselecting ad account. Please try again.' 
        })
        return
      }
      this.loadingSubmit = true
      const useDefault = true
      try {
        const { data: lensData } = await LensAPI.Lens.get(this.lensId)
        const { data: report } = await LensAPI.LensReport.create({
          lens_id: this.lensId,
          name,
          description: desc,
          type: type?.key ?? this.preSelectedReportType,
          selected_ad_accounts: adAccounts,
          filters: [
            [{ key: 'event_date', operation: 'greater_than', value: 'last_7_days' }],
            [{ key: 'event_date', operation: 'less_than', value: 'today' }]
          ],
          table_columns: [],
          group_by: 'ad_name',
          graph_type: 'grid',
          click_attribution_window: lensData.click_attribution_window,
          view_attribution_window: lensData.view_attribution_window,
          selected_columns: {
            bar: ['spend', 'roas', 'ctr', 'cpm'],
            line: ['spend', 'roas'],
            grid: ['spend', 'roas', 'ctr', 'cpm']
          },
          selected_graph_rows: {
            bar: 4,
            line: 4,
            grid: 4
          },
          sorted_column: { by: 'spend', order: 'desc' },
          table_config: {
            showStatus: true,
            showTags: false,
            showCreationRange: false,
            colorFormat: 'positive'
          },
          folder_id: this.selectedFolder?.id
        }, useDefault)
        const reportId = report?.id
        this.$router.push({ name: 'LensReport', params: { lensId: this.lensId, reportId } })
        this.fetchSidebarLenses()
        this.$emit('close')
      } catch (error) {
        console.error(error)
        this.$showAlert({ type: 'error', message: 'Failed to create report. See console for details.' })
        this.$emit('close')
      } finally {
        this.loadingSubmit = false
      }
    },

    // ====== UI Methods ======
    updateSelectedFolder (folder) {
      this.selectedFolder = folder
    },
    handleAdAccountSelect (account) {
      const currentAccountIds = this.reportForm.adAccounts
      if (this.currentSelectionPlatform !== account.platform) {
        this.reportForm.adAccounts = [account.id]
      } else if (currentAccountIds.includes(account.id)) {
        this.reportForm.adAccounts = currentAccountIds.filter(id => id !== account.id)
      } else {
        this.reportForm.adAccounts = [...currentAccountIds, account.id]
      }
    },
    handleBack () {
      if (this.isFirstStep) this.$emit('close')
      else this.stepIndex--
    },
    handleNext () {
      if (this.isLastStep) this.handleSubmit()
      else this.stepIndex++
    }
  }
}
</script>

<style scoped>
.report-type-btn {
  border-radius: 12px;
  border: 1px solid;
  border-color: #EAEFF3;
  background-color: transparent;
  transition: border-color 100ms ease-in-out, background-color 100ms ease-in-out;
}
.report-type-btn:hover {
  border-color: #DFE1E6;
}
.report-type-btn.active, .report-type-btn.active:hover {
  border-color: transparent;
  background-color: #F6F8FA;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  position: relative;
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out,
    width 200ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.fade-in {
  animation: fadeIn 200ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.next-chevron {
  width: 24px;
  height: 20px;
  position: relative;
  overflow: hidden;
}
.info-tooltip {
  position: absolute;
  width: 280px;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>